import { createContext } from 'react';
/** The source side a new edge gets when nothing else has set one yet.
 *  Follows the primary flow direction so a plain graph still reads left to
 *  right (or top to bottom) by default. */
export function defaultSourceSide(direction) {
    return direction === 'TB' ? 'bottom' : 'right';
}
export function defaultTargetSide(direction) {
    return direction === 'TB' ? 'top' : 'left';
}
/** Stable fallback target handle rendered at every node's target midpoint.
 *  Edges whose source is not yet in the target's slot list reference this
 *  anchor so the handle exists before React Flow validates them. */
export const TARGET_ANCHOR_ID = 'target-anchor';
/** The exit handle of a fixed transition row that has no arrow yet. */
export const EXIT_HANDLE_ID = 'exit';
/** The one handle every arrow into a node card enters by: a dot on the
 *  card's left edge, level with its header. */
export const ENTRY_HANDLE_ID = 'entry';
/** Stable anchor handle rendered on every source side at the midpoint.
 *  Draft edges attach here while the operator is choosing a condition — the
 *  handle exists before the edge, so React Flow never races. Once the
 *  condition is picked, the edge migrates to its real slot handle. */
export const SOURCE_ANCHOR_ID = 'source-anchor';
// Each outgoing edge belongs to a "slot" on its source node, one per arrow,
// so arrows to different targets get distinct handles at distinct pixels and
// fan out without any render-time offset trick.
/** Source slot id for an edge — keyed by the (source, target) pair plus
 *  the side (right or bottom) the edge exits from. */
export function sourceSlotFor(edge) {
    return `src-${edge.side}:${edge.source}->${edge.target}`;
}
/** Handle-id prefix of a draft transition row. A connection from it turns
 *  the draft into an arrow. */
export const DRAFT_HANDLE_PREFIX = 'draft:';
/** Base spacing between slot handles, in percent of the node's cross-axis
 *  dimension. Picked small so adding a sibling edge lands right next to the
 *  existing group rather than reshuffling to the thirds. */
const SLOT_BASE_SPACING_PERCENT = 10;
/** Maximum total span the cluster is allowed to occupy, in percent. Prevents
 *  slots from drifting off the node when the group grows beyond what the
 *  base spacing supports. */
const SLOT_MAX_SPAN_PERCENT = 80;
/** Compute the top (or left in TB) percent for slot `index` out of `count`
 *  slots, clustered around the 50% midpoint. One slot → 50%. Two slots →
 *  45% / 55%. Each new slot joins the group right next to its neighbors
 *  (base spacing). Once the natural span would exceed the max, spacing
 *  shrinks to keep every slot on the node. */
export function slotOffsetPercent(index, count) {
    if (count <= 1)
        return 50;
    const naturalSpan = (count - 1) * SLOT_BASE_SPACING_PERCENT;
    const spacing = naturalSpan > SLOT_MAX_SPAN_PERCENT
        ? SLOT_MAX_SPAN_PERCENT / (count - 1)
        : SLOT_BASE_SPACING_PERCENT;
    return 50 + (index - (count - 1) / 2) * spacing;
}
/**
 * Shared context so canvas nodes (StepNode, FormNode, …) can read common
 * live-call and layout meta without the editor having to prop-drill through
 * every ReactFlow node render.
 */
export const FlowMetaContext = createContext({
    reachable: new Set(),
    activeNodeId: null,
    previousNodeId: null,
    direction: 'LR',
    implicitSources: new Set(),
    sourceSlotsByNode: new Map(),
    targetSlotsByNode: new Map(),
    edgeHighlightedNodeIds: new Set(),
    edgeLabels: new Map(),
    transitionsBySource: new Map(),
    selectArrow: () => { },
    deleteArrow: () => { },
    moveListRow: () => { },
    draftsBySource: new Map(),
    addDraft: () => { },
    selectDraft: () => { },
    removeDraft: () => { },
    bendArrow: () => { },
    nodeActionGroupsById: {},
    transferActionReferencesById: {},
    triggerLabel: null,
    triggerChannels: [],
    triggerChoices: [],
    fixedEntry: false,
    channelsReachingNode: new Map(),
});
//# sourceMappingURL=FlowEditorContext.js.map